import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Button } from 'react-bootstrap';

async function cropWithQuad(dataUrl, quad, cropTarget) {
    return new Promise((resolve, reject) => {
        if (!window.cv || !quad || quad.length !== 4) {
            resolve(dataUrl); // fallback: return original if no quad
            return;
        }
        const img = new window.Image();
        img.onload = () => {
            let src = cv.imread(img);
            let pts = sortPoints(quad);

            const widthA = Math.hypot(pts[2].x - pts[3].x, pts[2].y - pts[3].y);
            const widthB = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
            const maxWidth = Math.max(widthA, widthB);

            const heightA = Math.hypot(pts[1].x - pts[2].x, pts[1].y - pts[2].y);
            const heightB = Math.hypot(pts[0].x - pts[3].x, pts[0].y - pts[3].y);
            const maxHeight = Math.max(heightA, heightB);

            let dstQuad = cv.matFromArray(4, 1, cv.CV_32FC2, [
                0, 0,
                maxWidth - 1, 0,
                maxWidth - 1, maxHeight - 1,
                0, maxHeight - 1
            ]);
            let srcQuad = cv.matFromArray(4, 1, cv.CV_32FC2, [
                pts[0].x, pts[0].y,
                pts[1].x, pts[1].y,
                pts[2].x, pts[2].y,
                pts[3].x, pts[3].y
            ]);

            let M = cv.getPerspectiveTransform(srcQuad, dstQuad);
            let dst = new cv.Mat();
            cv.warpPerspective(src, dst, M, new cv.Size(maxWidth, maxHeight));

            // Convert to grayscale
            let docGray = new cv.Mat();
            cv.cvtColor(dst, docGray, cv.COLOR_RGBA2GRAY, 0);

            // Increase contrast by 10%
            let contrasted = new cv.Mat();

            // Apply sharpening filter to make letters less diffused
            let sharpened = new cv.Mat();
            let kernel;
            // Standard sharpening
            cv.convertScaleAbs(docGray, contrasted, 1.2, -20);
            kernel = cv.matFromArray(3, 3, cv.CV_32F, [
                0, -1,  0,
              -1,  5, -1,
                0, -1,  0
            ]);
            
            cv.filter2D(contrasted, sharpened, cv.CV_8U, kernel);

            // Convert result to base64
            let canvas = document.createElement('canvas');
            cv.imshow(canvas, sharpened);
            resolve(canvas.toDataURL('image/jpeg', 0.95));

            // Clean up
            src.delete(); dst.delete(); dstQuad.delete(); srcQuad.delete();
            docGray.delete(); contrasted.delete(); sharpened.delete(); kernel.delete();
        };
        img.onerror = () => resolve(dataUrl); // fallback
        img.src = dataUrl;
    });

    function sortPoints(pts) {
        pts = pts.slice().sort((a, b) => a.y - b.y);
        let top = pts.slice(0, 2).sort((a, b) => a.x - b.x);
        let bottom = pts.slice(2, 4).sort((a, b) => a.x - b.x);
        return [top[0], top[1], bottom[1], bottom[0]];
    }
}

function CameraCaptureModal({ show, onClose, onCapture }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  // Portrait default: width < height
  const [canvasSize, setCanvasSize] = useState({ width: 1920, height: 2320 });
  const [cropTarget, setCropTarget] = useState('A'); // Default to cedula (A or B)

  useEffect(() => {
    let animationId;

    async function processFrame() {
      if (!videoRef.current || !canvasRef.current) {
        animationId = requestAnimationFrame(processFrame);
        return;
      }
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Always draw portrait
      canvas.width = canvasSize.width;
      canvas.height = canvasSize.height;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      if (cropTarget === 'A' || cropTarget === 'B') {
        // Colombian cédula aspect ratio
        const cedulaAspect = 8.56 / 5.4; // ≈ 1.585
        let rectWidth, rectHeight;
        // Fit the rectangle to 70% of the canvas width, keep aspect ratio
        rectWidth = canvas.width;
        rectHeight = rectWidth / cedulaAspect;
        const x = 0; // Start at left
        const y = 0; // Start at top
        ctx.save();
        ctx.strokeStyle = 'green';
        ctx.lineWidth = 5;
        ctx.strokeRect(x, y, rectWidth, rectHeight);
        ctx.restore();
      } else if (cropTarget === 'COMPROBANTE') {
        let rectWidth = canvas.width;
        let rectHeight = canvas.height / 2;
        const x = 0; // Start at left
        const y = 0; // Start at top
        ctx.save();
        ctx.strokeStyle = 'blue';
        ctx.lineWidth = 5;
        ctx.strokeRect(x, y, rectWidth, rectHeight);
        ctx.restore();
      }else if (cropTarget === 'DOCUMENTO') {
        // Letter size aspect ratio: 8.5 x 11 inches = 1.294
        const letterAspect = 11 / 8.5; // ≈ 1.294
        // Fit the rectangle to the full canvas width, keep aspect ratio
        let rectWidth = canvas.width;
        let rectHeight = rectWidth * letterAspect;
        if (rectHeight > canvas.height) {
            // If height exceeds canvas, fit to canvas height instead
            rectHeight = canvas.height;
            rectWidth = rectHeight / letterAspect;
        }
        const x = (canvas.width - rectWidth) / 2;
        const y = (canvas.height - rectHeight) / 2;
        ctx.save();
        ctx.strokeStyle = 'yellow';
        ctx.lineWidth = 5;
        ctx.strokeRect(x, y, rectWidth, rectHeight);
        ctx.restore();      
      }
      else {
        ctx.save();
        ctx.strokeStyle = 'red';
        ctx.lineWidth = 5;
        ctx.strokeRect(0, 0, canvas.width, canvas.height);
        ctx.restore();
      }

      animationId = requestAnimationFrame(processFrame);
    }

    if (show && window.cv) {
      animationId = requestAnimationFrame(processFrame);
    }

    return () => {
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, [show, cropTarget, canvasSize]);

  useEffect(() => {
    let localStream = null;
    async function startCamera() {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        alert("La cámara no está disponible en este navegador o dispositivo.");
        onClose();
        return;
      }
      // Request portrait video
      navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
        }
      })
      .then(s => {
        setStream(s);
        localStream = s;
        if (videoRef.current) videoRef.current.srcObject = s;
        const [track] = s.getVideoTracks();
        const capabilities = track.getCapabilities();
        if (capabilities.zoom) {
          track.applyConstraints({ advanced: [{ zoom: capabilities.zoom.min }] });
        }
      })
      .catch(err => {
        alert("No se pudo acceder a la cámara: " + err.message);
        onClose();
      });
    }
    if (show) startCamera();
    return () => {
      if (localStream) localStream.getTracks().forEach(track => track.stop());
      if (stream) stream.getTracks().forEach(track => track.stop());
    };
    // eslint-disable-next-line
  }, [show]);

  const handleCapture = async () => {
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    const width = canvasSize.width;
    const height = canvasSize.height;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, width, height);

    const dataUrl = canvas.toDataURL('image/jpeg');

    // Calculate the guide rectangle (same as in processFrame)
    let quad = null;
    if (cropTarget === 'A' || cropTarget === 'B') {
      const cedulaAspect = 8.56 / 5.4;
      let rectWidth = width;
      let rectHeight = rectWidth / cedulaAspect;
      const x = 0; // Start at left
      const y = 0; // Start at top
      quad = [
        { x: x, y: y }, // top-left
        { x: x + rectWidth, y: y }, // top-right
        { x: x + rectWidth, y: y + rectHeight }, // bottom-right
        { x: x, y: y + rectHeight } // bottom-left
      ];
    } else if (cropTarget === 'COMPROBANTE') {
      let rectWidth = width;
      let rectHeight = height / 2;
      const x = 0;
      const y = 0;
      quad = [
        { x: x, y: y }, // top-left
        { x: x + rectWidth, y: y }, // top-right
        { x: x + rectWidth, y: y + rectHeight }, // bottom-right
        { x: x, y: y + rectHeight } // bottom-left
      ];
    } else if (cropTarget === 'DOCUMENTO') {
        // Letter size aspect ratio: 8.5 x 11 inches = 1.294
        const letterAspect = 11 / 8.5;
        let rectWidth = width;
        let rectHeight = rectWidth * letterAspect;
        if (rectHeight > height) {
            rectHeight = height;
            rectWidth = rectHeight / letterAspect;
        }
        const x = (width - rectWidth) / 2;
        const y = (height - rectHeight) / 2;
        quad = [
            { x: x, y: y }, // top-left
            { x: x + rectWidth, y: y }, // top-right
            { x: x + rectWidth, y: y + rectHeight }, // bottom-right
            { x: x, y: y + rectHeight } // bottom-left
        ];
    }

    // Crop if quad is present
    let finalImage = dataUrl;
    if (quad) {
      finalImage = await cropWithQuad(dataUrl, quad);
    }

    onCapture(finalImage, quad);
    onClose();
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="xl"
      dialogClassName="modal-fullscreen"
      contentClassName="p-0"
      style={{ padding: 0 }}
    >
      <Modal.Header closeButton style={{ border: "none", background: "#000", color: "#fff" }}>
        <Modal.Title style={{ color: "#fff" }}>
          <div style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "center",
            gap: 8,
            marginBottom: 16,
            position: "absolute",
            top: 16,
            left: 0,
            width: "100vw",
            zIndex: 10,
            padding: "0 8px"
          }}>
            <Button
              size="sm"
              style={{ minWidth: 90, flex: "1 1 90px" }}
              variant={cropTarget === 'A' ? "primary" : "outline-primary"}
              onClick={() => setCropTarget('A')}
            >
              Cédula
            </Button>
            <Button
              size="sm"
              style={{ minWidth: 90, flex: "1 1 90px" }}
              variant={cropTarget === 'COMPROBANTE' ? "primary" : "outline-primary"}
              onClick={() => setCropTarget('COMPROBANTE')}
            >
              Comprobante
            </Button>
            <Button
              size="sm"
              style={{ minWidth: 90, flex: "1 1 90px" }}
              variant={cropTarget === 'DOCUMENTO' ? "primary" : "outline-primary"}
              onClick={() => setCropTarget('DOCUMENTO')}
            >
              Documento
            </Button>
            <Button
              size="sm"
              style={{ minWidth: 90, flex: "1 1 90px" }}
              variant={cropTarget === 'EVIDENCIA' ? "primary" : "outline-primary"}
              onClick={() => setCropTarget('EVIDENCIA')}
            >
              Foto
            </Button>
            <Button
              size="sm"
              style={{ minWidth: 90, flex: "1 1 90px" }}
              variant="secondary"
              onClick={onClose}
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              style={{ minWidth: 90, flex: "1 1 90px" }}
              variant="primary"
              onClick={handleCapture}
            >
              Capturar
            </Button>
          </div>
        </Modal.Title>
        
      </Modal.Header>
      <Modal.Body
        style={{
          textAlign: 'center',
          padding: 0,
          position: 'relative',
          width: '100vw',
          height: '100vh',
          background: "#000",
          display: "block"
        }}
      >        
        <div style={{
          width: canvasSize.width,
          height: canvasSize.height,
          background: "#000",
          position: "relative"
        }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            width={canvasSize.width}
            height={canvasSize.height}
            style={{
              width: '100vw',           // Fit to viewport width
              height: '100vh',          // Fit to viewport height
              objectFit: 'contain',     // Keep aspect ratio
              background: "#000",
              borderRadius: 0,
              position: "absolute",
              top: 0,
              left: 0,
              zIndex: 1
            }}
          />
          <canvas
            ref={canvasRef}
            width={canvasSize.width}
            height={canvasSize.height}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: '100vw',           // Fit to viewport width
              height: '100vh',          // Fit to viewport height
              pointerEvents: "none",
              zIndex: 2
            }}
          />
        </div>
      </Modal.Body>      
    </Modal>
  );
}

export default CameraCaptureModal;
export { cropWithQuad };